import { useApolloClient } from '@apollo/client';
import {
  RequestCloudinaryUploadSignatureMutation,
  RequestCloudinaryUploadSignatureMutationVariables,
} from '@graphql/admin/generated/graphql.react';
import { requestCloudinaryUploadSignatureMutation } from '@graphql/admin/mutations';
import { handleError } from '@utils/error';
import { Modal, Upload, message, Divider, Alert, Button } from 'antd';
import buildUrl from 'cloudinary-build-url';
import useTranslation from 'next-translate/useTranslation';
import { useRef, useState } from 'react';
import {
  ImageAnnotationCanvas,
  ImageAnnotationCanvasRefType,
} from '../ImageAnnotationCanvas';
import { useUploadImage } from './useUploadImage';

type Props = {
  open?: boolean;
  onDone?: (url: string) => void;
  onClose?: () => void;
};

export function ImageDrawAndUploadModal({ open, onDone, onClose }: Props) {
  const { t } = useTranslation('components/input/image-draw-and-upload-modal');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, uploadImage] = useUploadImage();
  const annotationRef = useRef<ImageAnnotationCanvasRefType | null>(null);

  const handleClose = () => {
    setImageUrl(null);
    onClose?.();
  };

  const handleReset = () => {
    annotationRef.current?.reset();
  };

  const handleDone = async () => {
    if (imageUrl) {
      if (!annotationRef.current?.dirty) {
        onDone?.(imageUrl);
        handleClose();
      } else {
        annotationRef.current?.canvasRef.current?.toBlob(
          async (blob) => {
            if (blob) {
              uploadImage(blob).then((result) => {
                const url = buildUrl(result.public_id, {
                  cloud: { cloudName: process.env.CLOUDINARY_NAME },
                  transformations: { resize: { type: 'scale', width: 1000 } },
                });
                onDone?.(url);
                handleClose();
              });
            }
          },
          'image/jpg',
          90
        );
      }
    }
  };

  return (
    <Modal
      destroyOnHidden
      title={t('title')}
      open={open}
      width={1000}
      closable={!uploading}
      maskClosable={!uploading}
      onCancel={handleClose}
      footer={
        <div className="w-full flex justify-end">
          {!!imageUrl && (
            <Button
              key="reset"
              htmlType="button"
              className="mr-auto"
              disabled={uploading}
              onClick={handleReset}
            >
              {t('global:reset')}
            </Button>
          )}
          <Button
            key="back"
            htmlType="button"
            disabled={uploading}
            onClick={handleClose}
          >
            {t('global:cancel')}
          </Button>
          <Button
            key="submit"
            htmlType="submit"
            type="primary"
            disabled={!imageUrl || uploading}
            loading={uploading}
            onClick={handleDone}
          >
            {t('global:done')}
          </Button>
        </div>
      }
    >
      <Upload.Dragger
        name="file"
        customRequest={async ({ file, onError, onSuccess, onProgress }) => {
          try {
            const result = await uploadImage(file);
            onSuccess?.(result);
          } catch (err) {
            onError?.(err);
            handleError(err, { t });
          }
        }}
        onChange={(info) => {
          const { status } = info.file;
          if (status === 'done') {
            const data = info.file.response as {
              url: string;
              public_id: string;
            };
            const url = buildUrl(data.public_id, {
              cloud: { cloudName: process.env.CLOUDINARY_NAME },
              transformations: { resize: { type: 'scale', width: 1000 } },
            });
            setImageUrl(url);
          }
        }}
      >
        <p className="ant-upload-text">{t('dropzone.text')}</p>
      </Upload.Dragger>
      {!!imageUrl && (
        <>
          <Divider />
          <Alert
            type="info"
            className="mb-4"
            message={t('annotation.notice')}
          />
          <div className="flex flex-col">
            <ImageAnnotationCanvas ref={annotationRef} imageUrl={imageUrl} />
          </div>
        </>
      )}
    </Modal>
  );
}
